import React, { useState } from 'react';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiUser, FiMail, FiDollarSign, FiCalendar, FiKey } from 'react-icons/fi';
import PasswordInput from '../components/PasswordInput';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Profile = () => {
  const { user, fetchUser } = useAuth();
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    currentPassword: ''
  });
  const [changingEmail, setChangingEmail] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/users/change-password`,
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();

    if (!emailForm.newEmail) {
      toast.error('Please enter a new email');
      return;
    }

    if (!emailForm.currentPassword) {
      toast.error('Please enter current password');
      return;
    }

    setChangingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/users/change-email`,
        {
          newEmail: emailForm.newEmail,
          currentPassword: emailForm.currentPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchUser();
      toast.success('Email changed successfully!');
      setEmailForm({
        newEmail: '',
        currentPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change email');
    } finally {
      setChangingEmail(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <div className="h-1 bg-blue-500 rounded mt-2"></div>
        </div>

        <div className="rdp-card p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
              <FiUser className="text-white" size={40} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{user.username}</h2>
              <p className="text-blue-100/70">{user.email}</p>
              {user.role === 'admin' && (
                <span className="inline-block mt-2 px-3 py-1 bg-blue-500/20 text-blue-200 rounded-full text-sm font-semibold border border-blue-400/30">
                  Admin
                </span>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <FiUser className="text-blue-300 mt-1" size={20} />
              <div>
                <p className="text-sm text-blue-100/70">Username</p>
                <p className="text-lg font-semibold text-white">{user.username}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <FiMail className="text-blue-300 mt-1" size={20} />
              <div>
                <p className="text-sm text-blue-100/70">Email</p>
                <p className="text-lg font-semibold text-white">{user.email}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <FiDollarSign className="text-blue-300 mt-1" size={20} />
              <div>
                <p className="text-sm text-blue-100/70">Balance</p>
                <p className="text-lg font-semibold text-white">€{user.balance?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <FiCalendar className="text-blue-300 mt-1" size={20} />
              <div>
                <p className="text-sm text-blue-100/70">Member Since</p>
                <p className="text-lg font-semibold text-white">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Change Email Section */}
        <div className="rdp-card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <FiMail className="text-blue-300" size={20} />
            <h2 className="text-xl font-bold text-white">Change Email</h2>
          </div>

          <form onSubmit={handleEmailChange} className="space-y-4">
            <div>
              <label className="block text-blue-100/80 font-medium mb-2">New Email</label>
              <input
                type="email"
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                placeholder="Enter new email"
                className="rdp-input"
                required
              />
            </div>

            <PasswordInput
              label="Current Password"
              labelClassName="block text-blue-100/80 font-medium mb-2"
              value={emailForm.currentPassword}
              onChange={(e) => setEmailForm({ ...emailForm, currentPassword: e.target.value })}
              placeholder="Enter current password"
              autoComplete="current-password"
              inputClassName="rdp-input w-full pr-11"
              required
            />

            <button
              type="submit"
              disabled={changingEmail}
              className="w-full rdp-primary-btn py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {changingEmail ? 'Changing Email...' : 'Change Email'}
            </button>
          </form>
        </div>

        {/* Change Password Section */}
        <div className="rdp-card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <FiKey className="text-blue-300" size={20} />
            <h2 className="text-xl font-bold text-white">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <PasswordInput
              label="Current Password"
              labelClassName="block text-blue-100/80 font-medium mb-2"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              placeholder="Enter current password"
              autoComplete="current-password"
              inputClassName="rdp-input w-full pr-11"
              required
            />

            <PasswordInput
              label="New Password"
              labelClassName="block text-blue-100/80 font-medium mb-2"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              placeholder="Enter new password"
              autoComplete="new-password"
              inputClassName="rdp-input w-full pr-11"
              required
            />

            <PasswordInput
              label="Confirm New Password"
              labelClassName="block text-blue-100/80 font-medium mb-2"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              placeholder="Confirm new password"
              autoComplete="new-password"
              inputClassName="rdp-input w-full pr-11"
              required
            />

            <button
              type="submit"
              disabled={changingPassword}
              className="w-full rdp-primary-btn py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
